import {
  EndOfNextPeriod,
  GetGranularityConfiguration,
  Granularity,
} from "@hydrologiq/common/types/reporting/Granularity"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { IANATimeZone, toTimestamp, startOfDay, endOfDay } from "@hydrologiq/common/helpers/date"
import Reporting, { ReportingSkeleton } from "@hydrologiq/shared/reporting/Reporting"
import { DateTime, days } from "timezonecomplete"
import { useState, useMemo, useEffect, useCallback } from "react"
import ReportingChart from "@hydrologiq/shared/reporting/ReportingChart"
import { ReportingChartSkeleton } from "@hydrologiq/shared/reporting/ReportingChart"
import CustomerReportingDownload from "./CustomerReportingDownload"
import { FieldSelector } from "@hydrologiq/shared/reporting/FieldSelector"
import { getAvailableFields } from "@hydrologiq/shared/reporting/usageCsv"
import { RowType } from "@hydrologiq/shared/reporting/rows"
import AssetSelector from "./AssetSelector"
import { CustomerUsage } from "@hydrologiq/gql/graphql"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { HStack } from "@chakra-ui/react"
import Informative from "@hydrologiq/shared/Informative"
import { useGetProfile } from "@hydrologiq/shared/providers/helpers/useProfile"
import { fetchCustomerReportingData, CustomerReportingResponse, CustomerReportingApiError } from "./customerReportingApi"

export type CustomerReportingUsageView = "chart" | "table"

/** The reporting data source type */
export type ReportingSource = "timescale" | "influxdb"

interface CustomerReportingUsageProps {
  start: DateTime
  end: DateTime
  granularity: Granularity
  customerId: string
  customerName: string
  view: CustomerReportingUsageView
  latest?: boolean
  title?: boolean
  /** The customer's reporting data source (from GraphQL Customer.source) */
  source?: ReportingSource
  /** The selected site's reporting data source (from GraphQL CustomerSite.source) */
  siteSource?: ReportingSource
  /** The selected site ID, if a site filter is active */
  selectedSiteId?: string
}

/**
 * Maps the InfluxDB-style queryFrequency values from the Granularity config
 * to the timescale API frequency parameter values.
 */
function mapGranularityToTimescaleFrequency(queryFrequency: string): string {
  switch (queryFrequency) {
    case "5min":
      return "5min"
    case "1H":
      return "hourly"
    case "1D":
      return "daily"
    case "1MS":
      return "monthly"
    case "1W-Sun":
      return "weekly"
    default:
      return "daily"
  }
}

/**
 * Get the auth token for API requests from localStorage.
 * Mirrors the pattern used in the systems HybridReporting component.
 */
function getAuthToken(): string {
  return localStorage.getItem("bearer_token") ?? ""
}

/**
 * Transforms a timescale API response row into the CustomerUsage shape
 * expected by the existing reporting display components.
 *
 * Maps Display_Field_Keys from the timescale response to the legacy
 * CustomerUsage field names used by the shared reporting components.
 */
function transformTimescaleRowToCustomerUsage(row: Record<string, unknown>): CustomerUsage {
  return {
    __typename: "CustomerUsage",
    start: (row.bucket_start as string) ?? "",
    end: (row.bucket_end as string) ?? "",
    // Generation fields
    averagePower: (row.generation_power_average as number) ?? null,
    peakPower: (row.generation_power_peak as number) ?? null,
    peakPowerTime: (row.generation_power_peak_time as string) ?? null,
    usedEnergy: (row.generation_energy as number) ?? null,
    // Emissions
    totalEmissions: (row.generation_co2e as number) ?? null,
    emissionsFactor: (row.generation_co2e_per_kwh as number) ?? null,
    // Hydrogen/weight fields
    usedWeight: (row.hydrogen_weight_consumed as number) ?? null,
    averageWeight: (row.hydrogen_weight_consumed as number) ?? null,
    startWeight: (row.hydrogen_weight_start as number) ?? null,
    endWeight: (row.hydrogen_weight_end as number) ?? null,
  }
}

export function CustomerReportingUsageSkeleton({ view, title }: { view: CustomerReportingUsageView; title?: boolean }) {
  return view === "table" ? <ReportingSkeleton /> : <ReportingChartSkeleton title={title} />
}

function CustomerReportingUsage({
  start,
  end,
  customerId,
  customerName,
  granularity,
  view,
  latest,
  title,
  source,
  siteSource,
  selectedSiteId,
}: CustomerReportingUsageProps) {

  const profile = useGetProfile()
  const [downloading, setDownloading] = useState<boolean>(false)
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([])

  // Timescale fetch state
  const [timescaleLoading, setTimescaleLoading] = useState<boolean>(false)
  const [timescaleData, setTimescaleData] = useState<CustomerUsage[] | null>(null)
  const [timescaleError, setTimescaleError] = useState<string | null>(null)
  const [timescaleAvailableFields, setTimescaleAvailableFields] = useState<string[]>([])

  // Determine active source: site source takes precedence when it differs from customer source
  const activeSource: ReportingSource = useMemo(() => {
    if (selectedSiteId && siteSource && siteSource !== source) {
      return siteSource
    }
    return source ?? "influxdb"
  }, [selectedSiteId, siteSource, source])

  const granularityConfig = useMemo(() => {
    return GetGranularityConfiguration(granularity)
  }, [granularity])

  const endOfPreview = useMemo(() => {
    let preview = EndOfNextPeriod(start, granularity, 10)
    if (preview.greaterThan(end)) preview = end
    return preview
  }, [start, end, granularity])

  // Initialise selected fields with all available fields
  const [selectedFields, setSelectedFields] = useState<(keyof RowType)[]>([])

  // Use GraphQL query with asset filtering support (influxdb path)
  const [getCustomerUsage, { loading: gqlLoading, data: gqlData }] = useLazyQueryWithEndpoint<gqlTypes.GetCustomerUsageQuery, gqlTypes.GetCustomerUsageQueryVariables>(gqlTypes.GetCustomerUsageDocument, undefined, "primary")

  // Fetch from timescale API
  const fetchTimescaleData = useCallback(async () => {
    setTimescaleLoading(true)
    setTimescaleError(null)

    try {
      const token = getAuthToken()
      const startISO = latest
        ? startOfDay(end.sub(days(9))).toIsoString()
        : start.toIsoString()
      const stopISO = latest
        ? endOfDay(end).toIsoString()
        : endOfPreview.toIsoString()
      const frequency = mapGranularityToTimescaleFrequency(granularityConfig.queryFrequency)
      const tz = IANATimeZone()

      // Request ALL registered display fields — the API response's meta.available_fields
      // tells us which ones actually have data for this customer
      const fields = [
        "generation_energy",
        "generation_power_average",
        "generation_power_peak",
        "generation_power_peak_time",
        "generation_co2e",
        "generation_co2e_per_kwh",
        "load_energy",
        "load_power_average",
        "load_power_peak",
        "load_power_peak_time",
        "hydrogen_weight_consumed",
        "hydrogen_weight_start",
        "hydrogen_weight_end",
        "diesel_fuel_consumed",
        "diesel_level_start",
        "diesel_level_end",
        "battery_soc_average",
        "battery_soc_peak",
        "battery_soc_min",
        "battery_discharge_energy",
        "battery_discharge_power_average",
        "battery_charge_energy",
        "battery_charge_power_average",
      ]

      const response: CustomerReportingResponse = await fetchCustomerReportingData(
        customerId,
        startISO,
        stopISO,
        frequency,
        fields,
        token,
        tz,
        selectedSiteId,
      )

      // Store which fields the API confirmed have actual data
      setTimescaleAvailableFields(response.meta.available_fields)

      const transformed = response.rows.map(transformTimescaleRowToCustomerUsage)
      setTimescaleData(transformed)
    } catch (err) {
      // 404 = no systems found for this customer — treat as empty data, not an error
      if (err instanceof CustomerReportingApiError && err.status === 404) {
        setTimescaleData([])
        setTimescaleAvailableFields([])
      } else {
        setTimescaleData(null)
        if (err instanceof Error) {
          setTimescaleError(err.message)
        } else {
          setTimescaleError("Failed to load reporting data.")
        }
      }
    } finally {
      setTimescaleLoading(false)
    }
  }, [customerId, start, end, endOfPreview, granularityConfig, latest, selectedSiteId])

  // Fetch data based on active source
  useEffect(() => {
    if (activeSource === "timescale") {
      fetchTimescaleData()
    } else if (selectedAssetIds.length > 0) {
      getCustomerUsage({
        variables: {
          id: customerId,
          start: toTimestamp(latest ? startOfDay(end.sub(days(9))) : start, true),
          end: toTimestamp(latest ? endOfDay(end) : endOfPreview, true),
          freq: granularityConfig.queryFrequency,
          location: IANATimeZone(),
          assetIds: selectedAssetIds.length > 0 ? selectedAssetIds.join(',') : '',
          installationIds: '',
          kinds: undefined
        }
      })
    }
  }, [activeSource, selectedAssetIds, start, end, granularityConfig, latest, customerId, selectedSiteId])

  // Determine the effective data rows and loading state based on active source
  const rows: CustomerUsage[] = useMemo(() => {
    if (activeSource === "timescale") {
      return timescaleData ?? []
    }
    return (gqlData?.getCustomerUsage?.data as CustomerUsage[]) || []
  }, [activeSource, timescaleData, gqlData])

  const loading = activeSource === "timescale" ? timescaleLoading : (gqlLoading || !selectedAssetIds.length)

  // Get available fields from data
  const availableFields = useMemo(() => {
    return getAvailableFields(rows)
  }, [rows])

  // Update selected fields when available fields change (first load)
  useEffect(() => {
    if (availableFields.length > 0 && selectedFields.length === 0) {
      setSelectedFields(availableFields)
    }
  }, [availableFields, selectedFields.length])

  // Memoized data callbacks (must be called unconditionally — React hooks rules)
  const tableDataCallback = useMemo(() => () => rows, [rows])
  const chartDataCallback = useMemo(() => () => rows, [rows])

  // Show friendly message for error states
  if (timescaleError && activeSource === "timescale") {
    return (
      <Informative
        heading="Unable to load reporting data"
        text={timescaleError}
      />
    )
  }

  // Show friendly message when no systems are linked (empty data after load)
  if (activeSource === "timescale" && !timescaleLoading && timescaleData !== null && timescaleData.length === 0 && timescaleAvailableFields.length === 0) {
    return (
      <Informative
        heading="No reporting data available"
        text="This customer has no systems with reporting data for the selected time range. Ensure systems are linked to this customer and have active monitoring."
      />
    )
  }

  return view === "table" ? (
    <Reporting
      start={start}
      end={end}
      granularity={granularity}
      onDataCallback={tableDataCallback}
      onDownload={() => setDownloading(true)}
      downloading={downloading}
      loading={loading}
      visibleFields={selectedFields}
      fieldSelector={
        <>
          {activeSource === "influxdb" && (
            <AssetSelector
              customerId={customerId}
              selectedAssetIds={selectedAssetIds}
              onSelectionChange={setSelectedAssetIds}
            />
          )}
          {availableFields.length > 0 && (
            <FieldSelector
              availableFields={availableFields}
              selectedFields={selectedFields}
              onFieldsChange={setSelectedFields}
              defaultFields={availableFields}
            />
          )}
        </>
      }
    >
      <CustomerReportingDownload
        start={start}
        end={end}
        granularity={granularity}
        customer={customerId}
        customerName={customerName}
        assetIds={selectedAssetIds}
        installationIds={[]}
        selectedFields={selectedFields}
        callback={() => setDownloading(false)}
        source={activeSource}
        selectedSiteId={selectedSiteId}
      />
    </Reporting>
  ) : (
    <>
      <HStack width={"100%"} justifyContent={"flex-end"}>
        {activeSource === "influxdb" && (
          <AssetSelector
            customerId={customerId}
            selectedAssetIds={selectedAssetIds}
            onSelectionChange={setSelectedAssetIds}
          />
        )}
      </HStack>

      <ReportingChart
        onDataCallback={chartDataCallback}
        title={title}
        loading={loading}
        fields={[profile === gqlTypes.TenantProfile.Rental ? "usedEnergy" : "usedWeight"]}
      />
    </>
  )
}

export default CustomerReportingUsage
